import { Headers } from '@/api/Headers';
import { HeaderRequest, HeaderResponse, HeadersRequest } from '@/api/data-contracts';
import useUploadProgressService from '@/shared/api/uploadProgress.service';

const useTablesService = () => {
  const {
    listHeaders: listHeadersRequest,
    addHeader: addHeaderRequest,
    updateHeader: updateHeaderRequest,
  } = useUploadProgressService(Headers);

  /**
   * `POST /headers/getHeaders` DOES filter on `resourceId` — but only when the field
   * is actually sent. The FE used to post `{ pageType }` alone and take `content[0]`,
   * so the grid got the first row of an unsorted page of EVERY row the org holds for
   * that page type: the test org held 25 MEDIA_PLAN rows spanning 4 different column
   * orders, and the same request seconds later came back with a different row first.
   * Grids rendered a random other resource's saved order. The write path could not
   * self-heal it either — it PATCHes the winning row's id while sending
   * `resourceId: ''`, which collides with the real org-level row on
   * `header_entity_page_type_org_user_resource_unique` and 400s, silently dropping
   * the user's column change.
   *
   * Send the resourceId explicitly, normalising absent to `''`. The backend then
   * returns exactly the one matching row (verified: `{ pageType, resourceId: '' }`
   * answers `totalElements: 1` against the same org where `{ pageType }` answers 25),
   * so the org-level row — the one every media-plan grid wants, since all media plans
   * share a single column order — can no longer be shadowed by a per-resource row.
   * The `find` is belt-and-braces in case the server-side filter ever regresses.
   */
  const listHeaders = async (
    query: HeadersRequest | undefined,
    signal?: AbortSignal | undefined,
  ): Promise<HeaderResponse | null> => {
    if (!query) {
      return null;
    }

    const resourceId = query.resourceId ?? '';

    try {
      const { data } = await listHeadersRequest(
        { ...query, resourceId },
        {
          signal,
        },
      );

      return (data?.content ?? []).find((row) => (row.resourceId ?? '') === resourceId) ?? null;
    } catch (_) {
      return null;
    }
  };

  const addHeader = async (request: HeaderRequest) => {
    const { data } = await addHeaderRequest(request);
    return data;
  };

  const updateHeader = async (headerId: string, request: HeaderRequest) => {
    const { data } = await updateHeaderRequest(headerId, request);
    return data;
  };

  return { listHeaders, addHeader, updateHeader };
};

export default useTablesService;
